import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Topbar } from '@/components/layout/Topbar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { api, formatApiError } from '@/lib/api';
import { WrWorkspace } from '@/components/wr/WrWorkspace';
import { WrAlerts } from '@/components/wr/WrAlerts';
import { WrConfig } from '@/components/wr/WrConfig';
import { WrPermissions } from '@/components/wr/WrPermissions';
import { Button } from '@/components/ui/button';
import { FileSpreadsheet } from 'lucide-react';
import { SuiteImportWizard } from '@/components/client/SuiteImportWizard';

export default function ClientWorkforceRecords() {
  const { t, i18n } = useTranslation();
  const isES = (i18n.language || 'es').startsWith('es');
  const [data, setData] = useState(null);
  const [alertCounts, setAlertCounts] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  const load = async () => {
    try {
      const r = await api.get('/wr/overview');
      setData(r.data);
      const a = await api.get('/wr/alerts');
      setAlertCounts(a.data.counts);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <div data-testid="client-wr-page">
      <Topbar title="Workforce Records" />
      <div className="p-6 lg:p-8 space-y-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t('wr.title')}</h1>
            <p className="text-base text-muted-foreground mt-1">{t('wr.subtitle')}</p>
          </div>
          {data?.is_admin && (
            <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="wr-import-btn">
              <FileSpreadsheet className="h-4 w-4 mr-1.5" />
              {t('wr.importExcel', { defaultValue: 'Importar desde Excel' })}
            </Button>
          )}
        </div>
        <SuiteImportWizard suite="workforcerecords" open={importOpen}
                           onClose={() => setImportOpen(false)} onImported={load} />
        {data && (
          data.is_admin ? (
            <Tabs defaultValue="matrix">
              <TabsList>
                <TabsTrigger value="matrix" data-testid="wr-tab-matrix">{t('wr.tabs.matrix')}</TabsTrigger>
                <TabsTrigger value="alerts" data-testid="wr-tab-alerts">
                  {isES ? 'Alertas de vencimiento' : 'Expiration alerts'}
                  {((alertCounts?.expired ?? data.kpis?.expired ?? 0) + (alertCounts?.expiring_soon ?? data.kpis?.expiring_soon ?? 0)) > 0 && (
                    <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-red-100 text-red-700 text-xs px-1.5 min-w-[1.25rem]"
                          data-testid="wr-tab-alerts-badge">
                      {(alertCounts?.expired ?? data.kpis?.expired ?? 0) + (alertCounts?.expiring_soon ?? data.kpis?.expiring_soon ?? 0)}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="config" data-testid="wr-tab-config">{t('wr.tabs.config')}</TabsTrigger>
                <TabsTrigger value="perms" data-testid="wr-tab-perms">{t('wr.tabs.perms')}</TabsTrigger>
              </TabsList>
              <TabsContent value="matrix" className="mt-6">
                <WrWorkspace t={t} data={data} canEdit
                  fetchDetail={(e, r) => api.get(`/wr/records/${e}/${r}`).then((x) => x.data)}
                  saveRecord={(e, r, p) => api.put(`/wr/records/${e}/${r}`, p).then((x) => x.data)}
                  onSaved={load} />
              </TabsContent>
              <TabsContent value="alerts" className="mt-6">
                <WrAlerts t={t} canEdit
                  fetchDetail={(e, r) => api.get(`/wr/records/${e}/${r}`).then((x) => x.data)}
                  saveRecord={(e, r, p) => api.put(`/wr/records/${e}/${r}`, p).then((x) => x.data)}
                  onChanged={load} />
              </TabsContent>
              <TabsContent value="config" className="mt-6">
                <WrConfig t={t} onChanged={load} />
              </TabsContent>
              <TabsContent value="perms" className="mt-6">
                <WrPermissions t={t} />
              </TabsContent>
            </Tabs>
          ) : (
            <WrWorkspace t={t} data={data} canEdit={data.can_edit} docEnabled={false}
              fetchDetail={(e, r) => api.get(`/wr/records/${e}/${r}`).then((x) => x.data)}
              saveRecord={(e, r, p) => api.put(`/wr/records/${e}/${r}`, p).then((x) => x.data)}
              onSaved={load} />
          )
        )}
      </div>
    </div>
  );
}
